import type { ThemePalette } from './palette';

/**
 * The three built-in palettes, in code so a project runs with no authored
 * assets - port of the Unity `ThemeLibrary`. A client would instead author a
 * palette object and hand it to the UX host; the runtime treats both alike.
 */

/** 1a - MRTK dark, cyan accent. */
export function darkCyan(): ThemePalette {
  return {
    name: 'DarkCyan',
    displayName: 'Dark · Cyan',
    isLight: false,
    root: '#10141b',
    plateColor: '#1e2430',
    plateAlpha: 0.94,
    overlayTint: '#ffffff',
    accent: '#4cc3e0',
    accentHover: '#72d3ea',
    onAccent: '#07222c',
    textHi: '#f2f5f9',
    textMid: '#9aa4b2',
    textLo: '#6b7686',
    secondaryText: '#c7cfda',
    danger: '#e25454',
  };
}

/** 1b - light frosted, teal accent (single-palette theme swap). */
export function lightTeal(): ThemePalette {
  return {
    name: 'LightTeal',
    displayName: 'Light · Teal',
    isLight: true,
    root: '#d8dde3',
    plateColor: '#fafcfd',
    plateAlpha: 0.9,
    overlayTint: '#1b2430',
    accent: '#0e9488',
    accentHover: '#12ab9d',
    onAccent: '#f3faf9',
    textHi: '#1b2430',
    textMid: '#5a6470',
    textLo: '#8a94a0',
    secondaryText: '#3a4450',
    danger: '#c0453f',
  };
}

/** 1c - hi-vis "focus mode", orange accent for gloved / bright-floor use. */
export function hiVisOrange(): ThemePalette {
  return {
    name: 'HiVisOrange',
    displayName: 'Hi-Vis · Orange',
    isLight: false,
    root: '#141210',
    plateColor: '#24201a',
    plateAlpha: 0.95,
    overlayTint: '#ffffff',
    accent: '#f27b2c',
    accentHover: '#ff9548',
    onAccent: '#241102',
    textHi: '#f7f3ed',
    textMid: '#b8b0a2',
    textLo: '#8d8578',
    secondaryText: '#d8d0c2',
    danger: '#e25454',
  };
}

/** All built-in palettes in cycle order. */
export function buildDefaultPalettes(): ThemePalette[] {
  return [darkCyan(), lightTeal(), hiVisOrange()];
}

/**
 * Holds the active palette and the set of selectable palettes, and raises a
 * change when the theme changes. The UI listens and re-themes - port of the
 * Unity `ThemeManager`.
 */
export class ThemeManager {
  private readonly palettes: ThemePalette[];
  private index: number;
  private readonly listeners = new Set<(palette: ThemePalette) => void>();

  constructor(palettes: ThemePalette[] = buildDefaultPalettes(), startIndex = 0) {
    if (palettes.length === 0) throw new Error('ThemeManager needs at least one palette');
    this.palettes = palettes;
    this.index = Math.min(Math.max(0, startIndex), palettes.length - 1);
  }

  get active(): ThemePalette {
    return this.palettes[this.index]!;
  }

  get all(): readonly ThemePalette[] {
    return this.palettes;
  }

  get activeIndex(): number {
    return this.index;
  }

  onChanged(listener: (palette: ThemePalette) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  select(index: number): void {
    const clamped = Math.min(Math.max(0, index), this.palettes.length - 1);
    if (clamped === this.index) return;
    this.index = clamped;
    this.listeners.forEach((listener) => listener(this.active));
  }

  next(): void {
    this.index = (this.index + 1) % this.palettes.length;
    this.listeners.forEach((listener) => listener(this.active));
  }
}
